export const AUTH_URL_CREDENTIAL_PARAMS = ["token", "t", "access_token", "refresh_token", "verificationToken", "next", "handoff"]

const TELEMETRY_CREDENTIAL_PARAMS = [...AUTH_URL_CREDENTIAL_PARAMS, "feedPreview", "checkoutToken", "checkout_token"]
const CONTINUATION_CREDENTIAL_PARAMS = ["phone", "code"]
const CHECKOUT_TOKEN_PATH = String.raw`^((?:(?:/[a-z]{2}){0,2}/(?:checkout|compra/comercio)|/order/(?:customer|payment-options))/)[^/]+`
const CONTINUATION_PATH = String.raw`^(?:/[a-z]{2}){0,2}/continuar-compra/`
const REDACTED_SEGMENT = "redacted"
const RELATIVE_BASE = "https://relative.invalid"

const normalizeKey = (key: string): string => key.replace(/[_-]/g, "").toLowerCase()
const credentialFields = new Set(TELEMETRY_CREDENTIAL_PARAMS.map(normalizeKey))

const isCredentialField = (key: string): boolean => credentialFields.has(normalizeKey(key))

interface RedactConfig {
  credentialKeys: string[]
  continuationKeys: string[]
  continuationPath: string
  tokenPath: string
  redacted: string
  relativeBase: string
}

const redactUrlValue = (value: string, config: RedactConfig): string => {
  const continuationPath = new RegExp(config.continuationPath)
  const tokenPath = new RegExp(config.tokenPath)
  const normalize = (key: string): string => key.replace(/[_-]/g, "").toLowerCase()
  const isRelative = (candidate: string): boolean => candidate.charAt(0) === "/" && candidate.charAt(1) !== "/"
  const isUrl = (candidate: string): boolean => isRelative(candidate) || /^https?:[/][/]/i.test(candidate)

  const scrub = (url: URL): boolean => {
    const continuation = continuationPath.test(url.pathname)
    const kept: Array<[string, string]> = []
    let changed = false
    url.searchParams.forEach((entry, key) => {
      const field = normalize(key)
      if (config.credentialKeys.indexOf(field) >= 0 || (continuation && config.continuationKeys.indexOf(field) >= 0)) {
        changed = true
        return
      }
      const next = isUrl(entry) ? redact(entry) : entry
      if (next !== entry) changed = true
      kept.push([key, next])
    })
    if (changed) url.search = new URLSearchParams(kept).toString()
    const pathname = url.pathname.replace(tokenPath, "$1" + config.redacted)
    if (pathname !== url.pathname) {
      url.pathname = pathname
      changed = true
    }
    return changed
  }

  const redact = (candidate: string): string => {
    if (!isUrl(candidate)) return candidate
    try {
      const url = new URL(candidate, config.relativeBase)
      if (!scrub(url)) return candidate
      return isRelative(candidate) ? url.pathname + url.search + url.hash : url.toString()
    } catch {
      return candidate
    }
  }

  return redact(value)
}

const REDACT_CONFIG: RedactConfig = {
  credentialKeys: TELEMETRY_CREDENTIAL_PARAMS.map(normalizeKey),
  continuationKeys: CONTINUATION_CREDENTIAL_PARAMS.map(normalizeKey),
  continuationPath: CONTINUATION_PATH,
  tokenPath: CHECKOUT_TOKEN_PATH,
  redacted: REDACTED_SEGMENT,
  relativeBase: RELATIVE_BASE,
}

const CREDENTIAL_PARAMS_CONFIG: RedactConfig = {
  ...REDACT_CONFIG,
  credentialKeys: AUTH_URL_CREDENTIAL_PARAMS.map(normalizeKey),
  continuationKeys: [],
  tokenPath: "(?!)",
}

export const redactAuthUrl = (value: string): string => redactUrlValue(value, CREDENTIAL_PARAMS_CONFIG)

const redactTelemetryUrl = (value: string): string => redactUrlValue(value, REDACT_CONFIG)

const EMBEDDED_URL =
  /https?:\/\/[^\s"'<>)\]]*[^\s"'<>)\].,;:!?]|(?<![\w/.:@-])(?:\/[a-z]{2}){0,2}\/(?:checkout|compra\/comercio|continuar-compra|order\/customer|order\/payment-options)\/[^\s"'<>)\]]*[^\s"'<>)\].,;:!?]/g

export const redactAuthText = (text: string): string => text.replace(EMBEDDED_URL, (match) => redactTelemetryUrl(match))

const isRelativeUrl = (value: string): boolean => value.startsWith("/") && !value.startsWith("//")

const MAX_DEPTH = 12
const REDACTED = "[redacted]"
const TRUNCATED = "[truncated]"

const isPlainRecord = (value: object): value is Record<string, unknown> => {
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const redactError = (error: Error, depth: number, ancestors: WeakSet<object>): Record<string, unknown> => ({
  name: error.name,
  message: redactAuthText(error.message),
  ...(error.stack ? { stack: redactAuthText(error.stack) } : {}),
  ...(error.cause === undefined ? {} : { cause: redactDeep(error.cause, depth + 1, ancestors) }),
})

const redactContainer = (value: object, depth: number, ancestors: WeakSet<object>): unknown => {
  if (value instanceof Error) return redactError(value, depth, ancestors)
  if (Array.isArray(value)) return value.map((entry) => redactDeep(entry, depth + 1, ancestors))
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, isCredentialField(key) ? REDACTED : redactDeep(entry, depth + 1, ancestors)]))
}

export const redactDeep = (value: unknown, depth = 0, ancestors = new WeakSet<object>()): unknown => {
  if (typeof value === "string") return isRelativeUrl(value) ? redactTelemetryUrl(value) : redactAuthText(value)
  if (value === null || typeof value !== "object") return value
  if (!(value instanceof Error) && !Array.isArray(value) && !isPlainRecord(value)) return value
  if (depth >= MAX_DEPTH || ancestors.has(value)) return TRUNCATED
  ancestors.add(value)
  try {
    return redactContainer(value, depth, ancestors)
  } finally {
    ancestors.delete(value)
  }
}

const redactInPlace = (event: object): void => {
  Object.assign(event, redactDeep(event))
}

export const redactAuthTelemetry = (event: object): void => redactInPlace(event)
